import React from "react";
import { BookingModal } from "./BookingModal";
import { BookingCard } from "./BookingCard";
import { BlockOutCard } from "./BlockOutCard";
import { type Booking, type BlockOut } from "../scheduleData";

interface TimeSlotCellProps {
  dateString: string;
  hour: number;
  hourBookings: Booking[];
  hourBlockOuts: BlockOut[];
  serviceColors: Record<string, string>;
  serviceColorsFromData?: Record<string, string>;
  onCreateBooking: (bookingData: any) => void;
  onEditBooking: (bookingId: string, bookingData: any) => void;
  onDeleteBooking: (bookingId: string) => void;
  onEditBlockOut: (blockOutId: string, data: any, otherPharmacyId?: string) => void;
  onDeleteBlockOut: (blockOutId: string, otherPharmacyId?: string) => void;
  pharmacistNames: string[];
  rooms: string[];
  size?: "week" | "day";
  readOnlyBookings?: boolean;
  isDarkMode?: boolean;
}

export function TimeSlotCell({
  dateString,
  hour,
  hourBookings,
  hourBlockOuts,
  serviceColors,
  serviceColorsFromData = {},
  onCreateBooking,
  onEditBooking,
  onDeleteBooking,
  onEditBlockOut,
  onDeleteBlockOut,
  pharmacistNames,
  rooms,
  size = "week",
  readOnlyBookings = false,
  isDarkMode = false,
}: TimeSlotCellProps) {
  const initialTime = `${hour.toString().padStart(2, "0")}:00`;
  const isPastSlot =
    new Date(`${dateString}T${initialTime}:00`).getTime() < Date.now();

  if (size === "day") {
    if (isPastSlot) {
      return (
        <div
          className={`flex-1 p-4 min-h-[80px] transition-colors duration-300 ${
            isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-50"
          }`}
        >
          <div className="space-y-2">
            {hourBookings.map((booking) => (
              <BookingCard
                key={booking.id}
                booking={booking}
                serviceColors={serviceColors}
                serviceColorsFromData={serviceColorsFromData}
                onEdit={onEditBooking}
                onDelete={onDeleteBooking}
                pharmacistNames={pharmacistNames}
                rooms={rooms}
                size="large"
                isDarkMode={isDarkMode}
                isReadOnly={readOnlyBookings}
              />
            ))}

            {hourBlockOuts.map((blockOut) => (
              <BlockOutCard
                key={blockOut.id}
                blockOut={blockOut}
                onEdit={onEditBlockOut}
                onDelete={onDeleteBlockOut}
                size="large"
                isDarkMode={isDarkMode}
              />
            ))}
          </div>
        </div>
      );
    }

    return (
      <BookingModal
        trigger={
          <div
            className={`flex-1 p-4 min-h-[80px] cursor-pointer transition-colors duration-300 ${
              isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-50"
            }`}
          >
            <div className="space-y-2">
              {hourBookings.map((booking) => (
                <BookingCard
                  key={booking.id}
                  booking={booking}
                  serviceColors={serviceColors}
                  serviceColorsFromData={serviceColorsFromData}
                  onEdit={onEditBooking}
                  onDelete={onDeleteBooking}
                  pharmacistNames={pharmacistNames}
                  rooms={rooms}
                  size="large"
                  isDarkMode={isDarkMode}
                  isReadOnly={readOnlyBookings}
                />
              ))}

              {/* Block-out periods */}
              {hourBlockOuts.map((blockOut) => (
                <BlockOutCard
                  key={blockOut.id}
                  blockOut={blockOut}
                  onEdit={onEditBlockOut}
                  onDelete={onDeleteBlockOut}
                  size="large"
                  isDarkMode={isDarkMode}
                />
              ))}
            </div>
          </div>
        }
        onSave={onCreateBooking}
        pharmacists={pharmacistNames}
        rooms={rooms}
        initialDate={dateString}
        initialTime={initialTime}
        isDarkMode={isDarkMode}
      />
    );
  }

  // Week view
  if (isPastSlot) {
    return (
      <div
        className={`p-2 h-16 border-r transition-colors duration-300 ${
          isDarkMode
            ? "border-gray-600 hover:bg-gray-700"
            : "border-gray-200 hover:bg-gray-50"
        }`}
      >
        {hourBookings.map((booking) => (
          <div key={booking.id} className="mb-1">
            <BookingCard
              booking={booking}
              serviceColors={serviceColors}
              serviceColorsFromData={serviceColorsFromData}
              onEdit={onEditBooking}
              onDelete={onDeleteBooking}
              pharmacistNames={pharmacistNames}
              rooms={rooms}
              size="small"
              isDarkMode={isDarkMode}
              isReadOnly={readOnlyBookings}
            />
          </div>
        ))}

        {hourBlockOuts.map((blockOut) => (
          <div key={blockOut.id} className="mb-1">
            <BlockOutCard
              blockOut={blockOut}
              onEdit={onEditBlockOut}
              onDelete={onDeleteBlockOut}
              size="small"
              isDarkMode={isDarkMode}
            />
          </div>
        ))}
      </div>
    );
  }

  return (
    <BookingModal
      trigger={
        <div
          className={`p-2 h-16 border-r cursor-pointer transition-colors duration-300 ${
            isDarkMode
              ? "border-gray-600 hover:bg-gray-700"
              : "border-gray-200 hover:bg-gray-50"
          }`}
        >
          {hourBookings.map((booking) => (
            <div key={booking.id} className="mb-1">
              <BookingCard
                booking={booking}
                serviceColors={serviceColors}
                serviceColorsFromData={serviceColorsFromData}
                onEdit={onEditBooking}
                onDelete={onDeleteBooking}
                pharmacistNames={pharmacistNames}
                rooms={rooms}
                size="small"
                isDarkMode={isDarkMode}
                isReadOnly={readOnlyBookings}
              />
            </div>
          ))}

          {/* Block-out periods */}
          {hourBlockOuts.map((blockOut) => (
            <div key={blockOut.id} className="mb-1">
              <BlockOutCard
                blockOut={blockOut}
                onEdit={onEditBlockOut}
                onDelete={onDeleteBlockOut}
                size="small"
                isDarkMode={isDarkMode}
              />
            </div>
          ))}
        </div>
      }
      onSave={onCreateBooking}
      pharmacists={pharmacistNames}
      rooms={rooms}
      initialDate={dateString}
      initialTime={initialTime}
      isDarkMode={isDarkMode}
    />
  );
}
